import React, { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Foldout from 'components/shared/Foldout';
import { Summary } from 'models/flexible/Summary';
import type { AdminSummaryShowFields } from 'services/api/fields';

type FullSummary = Summary<AdminSummaryShowFields<'attachments' | 'aiArtifact' | 'patientDemographics' | 'aiPrompt'>>;
import { colors } from 'styles/theme/colors';
import CopyIcon from 'svg/CopyIcon';

import PatientDemographics from './PatientDemographics';
import SourceList from './SourceList';
import SummarySection from './SummarySection';

const TABS = ['Summary', 'Detail'] as const;
type Tab = (typeof TABS)[number];

type SummaryProps = { summary: FullSummary | null };

function SummarySections({ data }: { data: any }) {
  return (
    <>
      {data.data.sections.map(({ title, attachmentIds, content }: any, idx: number) => (
        <SummarySection
          key={`section-${idx}`}
          title={title}
          attachments={attachmentIds.flatMap(
            (x: any) => data.data.sources?.find(({ attachmentId }: any) => attachmentId === x) || []
          )}
          content={content}
        />
      ))}
    </>
  );
}

function PatientDemographicsSection({ summary }: SummaryProps) {
  if (!summary?.patientDemographics || Object.values(summary.patientDemographics).every((v) => !v)) {
    return <EmptyText>No patient demographics available</EmptyText>;
  }
  return <PatientDemographics demographics={summary.patientDemographics} />;
}

function AIPromptSection({ summary }: SummaryProps) {
  if (!summary?.aiPrompt) return <EmptyText>No prompt available</EmptyText>;
  return <span>{summary.aiPrompt.name}</span>;
}

function SourcesSection({ summary }: SummaryProps) {
  if (!summary?.attachments?.length) return <EmptyText>No non-errored sources available</EmptyText>;
  return <SourceList summary={summary} />;
}

function ArtifactSection({ summary }: SummaryProps) {
  if (!summary?.aiArtifact?.data) return <EmptyText>No summary output available</EmptyText>;

  const artifact = summary.aiArtifact;

  if (!artifact.data.sections) {
    return <ArtifactText>{JSON.stringify(artifact, null, 2)}</ArtifactText>;
  }

  return (
    <SummaryContainer>
      <SummarySections data={artifact} />
    </SummaryContainer>
  );
}

function SummaryTabContent({ summary }: SummaryProps) {
  const errorMessage = summary?.error
    ? typeof summary.error === 'string'
      ? summary.error
      : JSON.stringify(summary.error, null, 2)
    : null;

  return (
    <SlideoutContent>
      {errorMessage && (
        <ErrorBanner>
          <ErrorTitle>Error</ErrorTitle>
          <ErrorText>{errorMessage}</ErrorText>
        </ErrorBanner>
      )}
      <SlideoutSection>
        <PatientDemographicsSection summary={summary} />
      </SlideoutSection>
      <SlideoutSection>
        <SectionLabel>Prompt:</SectionLabel>
        <AIPromptSection summary={summary} />
      </SlideoutSection>
      <SlideoutSection>
        <SectionLabel>Sources:</SectionLabel>
        <SourcesSection summary={summary} />
      </SlideoutSection>
      <SlideoutSection>
        <SectionLabel>Summary Output:</SectionLabel>
        <ArtifactSection summary={summary} />
      </SlideoutSection>
    </SlideoutContent>
  );
}

function DetailTabContent({ summary }: SummaryProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (summary) {
      navigator.clipboard.writeText(JSON.stringify(summary, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <SlideoutContent>
      <CopyButtonRow>
        <CopyButton onClick={handleCopy} title={copied ? 'Copied!' : 'Copy JSON to clipboard'}>
          {copied ? <CopiedText>Copied!</CopiedText> : <CopyIcon width={18} height={18} />}
        </CopyButton>
      </CopyButtonRow>
      <ArtifactText>{JSON.stringify(summary, null, 2)}</ArtifactText>
    </SlideoutContent>
  );
}

type Props = {
  show: boolean;
  summary: FullSummary | null;
  loading?: boolean;
  onClose: () => void;
};

export default function SummaryDetailSlideout({ show, summary, loading, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('Summary');

  const title = loading
    ? ''
    : summary
      ? `${summary.user?.name || 'Unknown User'} - ${summary.title} Summary`
      : 'Summary Details';

  return (
    <Foldout
      open={show}
      position='right'
      closeOnBackdropClick
      setOpen={(open) => {
        if (!open) {
          onClose();
          setActiveTab('Summary');
        }
      }}
      width='min(85%, 917px)'>
      <PanelContainer>
        <PanelHeader>
          <PanelTitle>{title}</PanelTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </PanelHeader>

        <TabBar>
          {TABS.map((tab) => (
            <TabButton key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
              {tab}
            </TabButton>
          ))}
        </TabBar>

        <PanelContent>
          {loading ? (
            <LoadingContainer>
              <CircularProgress style={{ margin: '0 auto', display: 'block' }} />
            </LoadingContainer>
          ) : activeTab === 'Summary' ? (
            <SummaryTabContent summary={summary} />
          ) : (
            <DetailTabContent summary={summary} />
          )}
        </PanelContent>

        <PanelFooter>
          <ButtonGroup>
            <Button onClick={onClose}>Close</Button>
          </ButtonGroup>
        </PanelFooter>
      </PanelContainer>
    </Foldout>
  );
}

// Styled components

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const PanelTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.black50};
  line-height: 1;
  flex-shrink: 0;
`;

const TabBar = styled.div`
  display: flex;
  gap: 24px;
  padding: 0 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? colors.blue100 : 'transparent')};
  padding: 10px 0;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black75)};
  cursor: pointer;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const PanelFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  display: flex;
  justify-content: flex-end;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  position: relative;
`;

const SlideoutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SlideoutSection = styled.div`
  margin-bottom: 8px;
`;

const SectionLabel = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${colors.black100};
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  color: ${colors.black50};
  font-style: italic;
`;

const ArtifactText = styled.pre`
  background-color: ${colors.black5};
  border-radius: 8px;
  padding: 16px;
  font-family: monospace;
  font-size: 13px;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`;

const ErrorBanner = styled.div`
  padding: 16px;
  background-color: ${colors.red10};
  border: 1px solid ${colors.red25};
  border-radius: 8px;
`;

const ErrorTitle = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: ${colors.red100};
  margin-bottom: 4px;
`;

const ErrorText = styled.pre`
  font-size: 12px;
  color: ${colors.black100};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const CopyButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CopyButton = styled.button`
  background: ${colors.black5};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${colors.black10};
  }
`;

const CopiedText = styled.span`
  font-size: 12px;
  color: ${colors.black50};
  font-weight: 500;
`;
